import React, { useEffect, useState } from "react";
import { Reveal, RedDot, SectionNum } from "./ui-primitives";

const FEED_URL =
  "https://api.rss2json.com/v1/api.json?rss_url=https%3A%2F%2Fdesignindya.substack.com%2Ffeed";

function stripHtml(html = "") {
  if (typeof window === "undefined" || !html) return "";
  // DOMParser does NOT execute scripts or fire <img onerror> handlers,
  // unlike `el.innerHTML = ...`. Safe for untrusted RSS HTML.
  try {
    const doc = new DOMParser().parseFromString(String(html), "text/html");
    return (doc.body.textContent || "").trim();
  } catch {
    return String(html).replace(/<[^>]*>/g, "").trim();
  }
}

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function SubstackFeed({ limit = 6 }) {
  const [state, setState] = useState({ status: "loading", items: [] });

  useEffect(() => {
    let cancelled = false;
    fetch(FEED_URL)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data && data.status === "ok" && Array.isArray(data.items)) {
          setState({
            status: "ok",
            items: data.items.slice(0, limit).map((it) => ({
              title: it.title,
              link: it.link,
              date: it.pubDate,
              excerpt: stripHtml(it.description).slice(0, 180),
              category:
                (Array.isArray(it.categories) && it.categories[0]) ||
                "Insights",
            })),
          });
        } else {
          setState({ status: "error", items: [] });
        }
      })
      .catch(() => !cancelled && setState({ status: "error", items: [] }));
    return () => {
      cancelled = true;
    };
  }, [limit]);

  if (state.status === "loading") {
    return (
      <div
        data-testid="substack-feed-loading"
        className="grid grid-cols-1 md:grid-cols-3"
        style={{ background: "var(--color-surface-container)", gap: "1px" }}
      >
        {["a", "b", "c"].map((slot) => (
          <div
            key={`substack-skeleton-${slot}`}
            style={{
              background: "var(--color-surface-container-lowest)",
              padding: "var(--space-32)",
              minHeight: 220,
            }}
          >
            <div
              style={{
                height: 12,
                width: "30%",
                background: "var(--color-outline-variant)",
                marginBottom: 28,
                opacity: 0.6,
              }}
            />
            <div
              style={{
                height: 18,
                width: "80%",
                background: "var(--color-outline-variant)",
                marginBottom: 10,
              }}
            />
            <div
              style={{
                height: 18,
                width: "55%",
                background: "var(--color-outline-variant)",
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  if (state.status === "error" || state.items.length === 0) {
    return (
      <div
        data-testid="substack-feed-empty"
        style={{
          background: "var(--color-surface-container-lowest)",
          padding: "var(--space-48)",
          textAlign: "center",
        }}
      >
        <p className="headline-md" style={{ marginBottom: 8 }}>
          Our Substack is just getting started.
        </p>
        <p
          className="body-md"
          style={{ color: "var(--color-on-surface-variant)", marginBottom: 20 }}
        >
          Subscribe and you'll be the first to receive new pieces.
        </p>
        <a
          href="https://designindya.substack.com"
          target="_blank"
          rel="noreferrer"
          data-testid="substack-feed-subscribe-empty"
          className="btn btn-accent"
        >
          Subscribe on Substack ↗
        </a>
      </div>
    );
  }

  const cols = Math.min(state.items.length, 3);
  const gridColsClass =
    cols === 1 ? "md:grid-cols-1" : cols === 2 ? "md:grid-cols-2" : "md:grid-cols-3";

  return (
    <div
      data-testid="substack-feed"
      className={`grid grid-cols-1 ${gridColsClass}`}
      style={{ background: "var(--color-surface-container)", gap: "1px" }}
    >
      {state.items.slice(0, 3).map((a, i) => (
        <Reveal key={a.link} delay={i * 100}>
          <a
            href={a.link}
            target="_blank"
            rel="noreferrer"
            data-testid={`substack-post-${i}`}
            style={{
              background: "var(--color-surface-container-lowest)",
              padding: "var(--space-32)",
              height: "100%",
              display: "block",
              textDecoration: "none",
              color: "inherit",
              transition: "background-color 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background =
                "var(--color-surface-container-low)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background =
                "var(--color-surface-container-lowest)")
            }
          >
            <div className="flex items-center justify-between mb-10">
              <SectionNum>{String(i + 1).padStart(2, "0")}</SectionNum>
              <span className="label-sm">{a.category}</span>
            </div>
            <h3
              className="headline-md"
              style={{ marginBottom: 16, minHeight: 72 }}
            >
              {a.title}
            </h3>
            {a.excerpt && (
              <p
                className="body-sm"
                style={{
                  color: "var(--color-on-surface-variant)",
                  marginBottom: 20,
                }}
              >
                {a.excerpt}…
              </p>
            )}
            <div
              className="flex items-center justify-between pt-4"
              style={{ borderTop: "1px solid var(--color-outline)" }}
            >
              <span className="label-sm">{formatDate(a.date)}</span>
              <RedDot />
            </div>
          </a>
        </Reveal>
      ))}
    </div>
  );
}
